import { useState, useCallback } from 'react';
import {
    simulateDeployTransaction,
    simulateClaimTransaction,
    TransactionStatus,
} from '../utils/transactionSimulator';

/**
 * useTransactions Hook - Transaction management
 *
 * Provides transaction execution and history tracking:
 * - Execute deploy/claim transactions
 * - Track transaction history
 * - Loading states
 * - Error handling
 *
 * This hook is independent and can be easily modified.
 */
export function useTransactions() {
    const [transactions, setTransactions] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [lastTransaction, setLastTransaction] = useState(null);

    /**
     * Execute deploy transaction
     */
    const executeDeploy = useCallback(async (amount, blocks) => {
        setIsProcessing(true);

        try {
            const result = await simulateDeployTransaction(amount, blocks);

            // Add to transaction history
            setTransactions(prev => [result, ...prev].slice(0, 50)); // Keep last 50
            setLastTransaction(result);

            setIsProcessing(false);
            return result;
        } catch (error) {
            console.error('Deploy transaction error:', error);
            setIsProcessing(false);
            throw error;
        }
    }, []);

    /**
     * Execute claim transaction
     */
    const executeClaim = useCallback(async (minerAmount, solAmount) => {
        setIsProcessing(true);

        try {
            const result = await simulateClaimTransaction(minerAmount, solAmount);

            // Add to transaction history
            setTransactions(prev => [result, ...prev].slice(0, 50));
            setLastTransaction(result);

            setIsProcessing(false);
            return result;
        } catch (error) {
            console.error('Claim transaction error:', error);
            setIsProcessing(false);
            throw error;
        }
    }, []);

    /**
     * Clear transaction history
     */
    const clearHistory = useCallback(() => {
        setTransactions([]);
        setLastTransaction(null);
    }, []);

    /**
     * Get successful transactions
     */
    const successfulTransactions = transactions.filter(
        tx => tx.status === TransactionStatus.SUCCESS
    );

    /**
     * Get failed transactions
     */
    const failedTransactions = transactions.filter(
        tx => tx.status === TransactionStatus.FAILED
    );

    return {
        transactions,
        isProcessing,
        lastTransaction,
        executeDeploy,
        executeClaim,
        clearHistory,
        successfulTransactions,
        failedTransactions,
        totalTransactions: transactions.length,
    };
}
